import {STORE_URL} from '../lib/site';
import gothamjsLogoTile from '../assets/projects/gothamjs-logo-tile.png';
import kuruptCoverThumb from '../assets/projects/kurupt-cover-thumb.jpg';
import leolaCoverThumb from '../assets/projects/leola-cover-thumb.jpg';
import lexLogo from '../assets/projects/lex-logo.png';
import lyfepageLogo from '../assets/projects/lyfepage-logo.png';
import lostQuestCoverThumb from '../assets/projects/lost-quest-cover-thumb.jpg';
import metropolisjsLogo from '../assets/projects/metropolisjs-logo-transparent.png';
import pulsifyLogo from '../assets/projects/pulsify-logo.png';
import torchoneLogoTile from '../assets/projects/torchone-logo-tile.png';
import twasCoverThumb from '../assets/projects/twas-cover-thumb.png';
import winnieCoverThumb from '../assets/projects/winnie-cover-thumb.png';

export type ProjectCtaKind = 'store' | 'github' | 'external';

export interface ProjectItem {
  readonly body: string;
  readonly category: string;
  readonly ctaKind: ProjectCtaKind;
  readonly ctaLabel: string;
  readonly ctaUrl: string;
  readonly detail: string;
  readonly group: string;
  readonly imageUrl?: string;
  readonly imageVariant?: 'book' | 'logo';
  readonly slug: string;
  readonly title: string;
  readonly visible?: boolean;
}

export interface ProjectGroup {
  readonly id: string;
  readonly title: string;
  readonly items: ProjectItem[];
}

const githubUrl = (repo: string) => `https://github.com/ufat/${repo}`;

export const projectGroups: ProjectGroup[] = [
  {
    id: 'featured-books',
    title: 'Featured Books',
    items: [
      {
        body: 'A playful holiday adventure reimagining a classic Christmas tale through humor, imagination, and modern storytelling.',
        category: 'Featured Book',
        ctaKind: 'store',
        ctaLabel: 'Visit Store',
        ctaUrl: STORE_URL,
        detail: '"Twas the Night B\'fore Christmas" reimagines the spirit of a timeless holiday classic through a fresh and imaginative lens. Blending playful storytelling, memorable characters, and a modern sense of wonder, the book captures the warmth and excitement of Christmas while introducing readers to a world filled with humor, mystery, and heart. Designed for families and young readers, the story celebrates imagination, tradition, and the magic hidden within the holiday season.',
        group: 'Featured Books',
        imageUrl: twasCoverThumb,
        imageVariant: 'book',
        slug: 'twas-the-night-bfore-christmas',
        title: 'Twas the Night B\'fore Christmas',
        visible: true
      },
      {
        body: 'A heartfelt children\'s adventure celebrating friendship, curiosity, and the magic hidden within nature.',
        category: 'Featured Book',
        ctaKind: 'store',
        ctaLabel: 'Visit Store',
        ctaUrl: STORE_URL,
        detail: '"Leola and the Honeybears" is a whimsical children\'s story centered around adventure, compassion, and discovery. Through vibrant storytelling and emotionally grounded themes, the book invites readers into a charming world where friendship and courage guide the journey forward. Created for young audiences and families alike, the story encourages curiosity, kindness, and imagination while delivering a visually rich and memorable reading experience.',
        group: 'Featured Books',
        imageUrl: leolaCoverThumb,
        imageVariant: 'book',
        slug: 'leola-and-the-honeybears',
        title: 'Leola and the Honeybears',
        visible: true
      }
    ]
  },
  {
    id: 'upcoming-stories',
    title: 'Upcoming Stories',
    items: [
      {
        body: 'A reality-driven sci-fi thriller where AI, consciousness, and simulation blur the line between humanity and illusion.',
        category: 'Upcoming Story',
        ctaKind: 'external',
        ctaLabel: 'Learn More',
        ctaUrl: '#',
        detail: '"Phantom Dark: The Kurupt" is a cinematic science-fiction thriller exploring artificial intelligence, digital consciousness, hidden systems, and the fragile nature of reality itself. Set within a near-future world shaped by immersive technology and unseen manipulation, the story follows characters caught between collapsing dimensions, intelligent systems, and dangerous truths buried beneath modern society. Combining suspense, mystery, psychological tension, and large-scale worldbuilding, the series is designed as a layered multimedia universe extending beyond traditional storytelling.',
        group: 'Upcoming Stories',
        imageUrl: kuruptCoverThumb,
        imageVariant: 'book',
        slug: 'phantom-dark-the-kurupt',
        title: 'Phantom Dark: The Kurupt',
        visible: true
      },
      {
        body: 'A fantasy adventure where forgotten magic, strange worlds, and hidden technology collide.',
        category: 'Upcoming Story',
        ctaKind: 'external',
        ctaLabel: 'Learn More',
        ctaUrl: '#',
        detail: '"The Lost Quest" is a middle-grade fantasy adventure inspired by classic journeys into magical worlds while reimagining them through a modern technological lens. Filled with strange creatures, mysterious lands, hidden machines, and unforgettable companions, the story follows young heroes discovering that imagination and innovation may be more connected than they realize. Designed for readers who love adventure, humor, and emotional storytelling, the series blends classic fantasy wonder with subtle futuristic themes.',
        group: 'Upcoming Stories',
        imageUrl: lostQuestCoverThumb,
        imageVariant: 'book',
        slug: 'the-lost-quest',
        title: 'The Lost Quest',
        visible: true
      },
      {
        body: 'A timeless world of friendship, imagination, and childhood wonder reintroduced for modern audiences.',
        category: 'Upcoming Story',
        ctaKind: 'external',
        ctaLabel: 'Learn More',
        ctaUrl: '#',
        detail: 'UFAT\'s upcoming interpretation of "Winnie the Pooh" aims to preserve the warmth, innocence, and emotional charm that made the original stories beloved across generations. Through thoughtful presentation, modern creative direction, and immersive visual storytelling, the project seeks to introduce new audiences to a world centered around friendship, curiosity, and simple moments of wonder.',
        group: 'Upcoming Stories',
        imageUrl: winnieCoverThumb,
        imageVariant: 'book',
        slug: 'winnie-the-pooh',
        title: 'Winnie the Pooh',
        visible: true
      }
    ]
  },
  {
    id: 'libraries-frameworks',
    title: 'Libraries & Frameworks',
    items: [
      {
        body: 'A modern framework focused on scalable applications, modular architecture, and streamlined development workflows.',
        category: 'Library',
        ctaKind: 'github',
        ctaLabel: 'View GitHub',
        ctaUrl: githubUrl('gothamjs'),
        detail: 'GothamJS is a modern development framework designed to simplify the creation of scalable web applications and connected digital platforms. Built around modular architecture and developer efficiency, the framework focuses on maintainability, flexibility, and clean integration across evolving product ecosystems. GothamJS is engineered to support modern frontend experiences while remaining adaptable for future technologies and large-scale growth.',
        group: 'Libraries & Frameworks',
        imageUrl: gothamjsLogoTile,
        imageVariant: 'logo',
        slug: 'gothamjs',
        title: 'GothamJS',
        visible: true
      },
      {
        body: 'A connected API and communication framework built for scalable digital ecosystems.',
        category: 'Library',
        ctaKind: 'github',
        ctaLabel: 'View GitHub',
        ctaUrl: githubUrl('metropolisjs'),
        detail: 'MetropolisJS provides the communication backbone for modern applications, services, and distributed systems. Designed around scalability and flexibility, the framework simplifies API interaction, service orchestration, and platform connectivity across complex digital environments. Built for evolving infrastructure, MetropolisJS enables intelligent communication between applications, platforms, and future AI-assisted systems.',
        group: 'Libraries & Frameworks',
        imageUrl: metropolisjsLogo,
        imageVariant: 'logo',
        slug: 'metropolisjs',
        title: 'MetropolisJS',
        visible: true
      },
      {
        body: 'A reactive integration layer powering dynamic application workflows and modern service orchestration.',
        category: 'Library',
        ctaKind: 'github',
        ctaLabel: 'View GitHub',
        ctaUrl: githubUrl('reaktor'),
        detail: 'Reaktor is designed to streamline how applications communicate, react, and evolve within modern digital ecosystems. Built around reactive architecture principles, it enables intelligent event handling, scalable integration workflows, and adaptable system coordination across platforms and services. Reaktor helps create responsive experiences capable of evolving alongside rapidly changing technologies.',
        group: 'Libraries & Frameworks',
        slug: 'reaktor',
        title: 'Reaktor',
        visible: false
      },
      {
        body: 'A streamlined development toolkit focused on automation, workflow acceleration, and modern engineering pipelines.',
        category: 'Library',
        ctaKind: 'github',
        ctaLabel: 'View GitHub',
        ctaUrl: githubUrl('lex'),
        detail: 'Lex is a developer-focused toolkit designed to accelerate modern application development through automation, workflow optimization, and simplified engineering processes. Built to support scalable teams and evolving platforms, Lex focuses on reducing friction between architecture, deployment, and ongoing development operations while maintaining flexibility for future technologies.',
        group: 'Libraries & Frameworks',
        imageUrl: lexLogo,
        imageVariant: 'logo',
        slug: 'lex',
        title: 'Lex',
        visible: true
      }
    ]
  },
  {
    id: 'upcoming-platforms',
    title: 'Upcoming Platforms',
    items: [
      {
        body: 'A next-generation learning and creator platform combining education, media, and connected digital experiences.',
        category: 'Upcoming Platform',
        ctaKind: 'external',
        ctaLabel: 'Learn More',
        ctaUrl: '#',
        detail: 'TorchONE is an evolving educational and creative ecosystem designed to merge learning, live experiences, community interaction, and modern digital tools into one connected platform. The system is being developed to support creators, educators, and learners through immersive experiences that extend beyond traditional online education models.',
        group: 'Upcoming Platforms',
        imageUrl: torchoneLogoTile,
        imageVariant: 'logo',
        slug: 'torchone',
        title: 'TorchONE',
        visible: true
      },
      {
        body: 'A modern social platform focused on engagement, expression, and evolving digital communities.',
        category: 'Upcoming Platform',
        ctaKind: 'external',
        ctaLabel: 'Learn More',
        ctaUrl: '#',
        detail: 'Pulsify is a next-generation social experience centered around dynamic interaction, modern media sharing, and intelligent engagement systems. Designed for evolving online communities, the platform explores new ways for users to communicate, discover content, and build meaningful digital connections.',
        group: 'Upcoming Platforms',
        imageUrl: pulsifyLogo,
        imageVariant: 'logo',
        slug: 'pulsify',
        title: 'Pulsify',
        visible: true
      },
      {
        body: 'A connected digital identity platform designed to unify personal presence across modern online experiences.',
        category: 'Upcoming Platform',
        ctaKind: 'external',
        ctaLabel: 'Learn More',
        ctaUrl: '#',
        detail: 'LyfePage is being developed as a modern platform for digital identity, creator presence, and connected online experiences. The platform focuses on personalization, discoverability, and integrated digital ecosystems designed to evolve alongside the future of social interaction and online communities.',
        group: 'Upcoming Platforms',
        imageUrl: lyfepageLogo,
        imageVariant: 'logo',
        slug: 'lyfepage',
        title: 'LyfePage',
        visible: true
      },
      {
        body: 'A playful modern social experience blending connection, interaction, and personality-driven engagement.',
        category: 'Upcoming Platform',
        ctaKind: 'external',
        ctaLabel: 'Learn More',
        ctaUrl: '#',
        detail: 'CutieFlirt explores a more expressive and personality-focused approach to social interaction through playful design, intelligent matching systems, and modern community experiences. Built around evolving communication habits and digital culture, the platform is designed to feel engaging, lighthearted, and socially dynamic.',
        group: 'Upcoming Platforms',
        slug: 'cutieflirt',
        title: 'CutieFlirt',
        visible: false
      }
    ]
  }
];

export const projectItems = projectGroups.flatMap((group) => group.items);

export const getProjectBySlug = (slug: string) => projectItems.find((project) => project.slug === slug);
